import {
  ArrowLeftIcon,
  CheckIcon,
  FilmStripIcon,
  ImageSquareIcon,
  SpinnerGapIcon,
  UploadSimpleIcon,
  WarningCircleIcon,
} from '@phosphor-icons/react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { CircleIconButton } from '@/components/ui/circle-icon-button';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Slider } from '@/components/ui/slider';
import { Switch } from '@/components/ui/switch';
import { trackProductEvent } from '@/lib/analytics/product-events';
import {
  type NativeVideoCoverMode,
  evaluateNativeVideoCoverReadiness,
  resolveNativeVideoCoverCapability,
} from '@/lib/platform-matrix/native-video-cover';
import type { Placement } from '@/lib/platform-matrix/placement-matrix';
import { SECTION_LABEL } from '@/lib/ui-tokens';
import { cn } from '@/lib/utils';
import {
  type TargetCoverAsset,
  type TargetCoverState,
  targetCoverService,
} from '@/services/data/TargetCoverService';

type EditorMode = 'auto' | 'frame' | 'upload';

export interface TargetCoverSummary {
  label: string;
  mode: NativeVideoCoverMode;
  blocking: boolean;
  issue: string | null;
  previewUrl: string | null;
}

interface TargetCoverEditorProps {
  targetId: string | null;
  videoUrl: string | null;
  durationSeconds?: number | null;
  focused?: boolean;
  onReview?: () => void;
  onDone?: () => void;
  onCancel?: () => void;
  onSummaryChange?: (summary: TargetCoverSummary) => void;
}

type NativeOptionsRecord = {
  shareToFeed?: boolean;
  cover?: {
    mode?: NativeVideoCoverMode;
    coverAssetId?: string;
    sourceRevisionId?: string;
    frameTimeMs?: number;
  };
  profileSurface?: {
    coverAssetId?: string;
    ratio?: string;
  };
};

function nativeOptions(value: unknown): NativeOptionsRecord {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? (value as NativeOptionsRecord)
    : {};
}

function editorModeFor(mode: NativeVideoCoverMode | undefined): EditorMode {
  return mode === 'frame' || mode === 'upload' ? mode : 'auto';
}

function formatTimestamp(milliseconds: number): string {
  const totalSeconds = Math.max(0, Math.round(milliseconds / 1000));
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
}

function modeLabel(mode: NativeVideoCoverMode): string {
  if (mode === 'frame') return 'Selected frame';
  if (mode === 'upload') return 'Uploaded cover';
  if (mode === 'product') return 'Product cover';
  return 'Automatic cover';
}

function CoverVisual({
  imageUrl,
  videoUrl,
  frameTimeMs,
  className,
  alt,
}: {
  imageUrl: string | null;
  videoUrl: string | null;
  frameTimeMs: number;
  className?: string;
  alt: string;
}) {
  const videoRef = useRef<HTMLVideoElement | null>(null);

  useEffect(() => {
    const video = videoRef.current;
    if (!video || imageUrl) return;
    const seek = () => {
      const bounded = Math.min(
        Math.max(0, frameTimeMs / 1000),
        Number.isFinite(video.duration) ? Math.max(0, video.duration - 0.05) : frameTimeMs / 1000,
      );
      try {
        video.currentTime = bounded;
      } catch {
        // Some browsers reject an early seek until metadata settles. The
        // loadedmetadata listener below gets a second deterministic chance.
      }
    };
    seek();
    video.addEventListener('loadedmetadata', seek);
    return () => video.removeEventListener('loadedmetadata', seek);
  }, [frameTimeMs, imageUrl]);

  if (imageUrl) {
    return <img src={imageUrl} alt={alt} className={cn('size-full object-cover', className)} />;
  }
  if (videoUrl) {
    return (
      <video
        ref={videoRef}
        src={videoUrl}
        muted
        playsInline
        preload="metadata"
        aria-label={alt}
        className={cn('size-full object-cover', className)}
      />
    );
  }
  return (
    <div className="flex size-full items-center justify-center bg-muted/30 text-muted-foreground">
      <FilmStripIcon className="size-6" weight="regular" aria-hidden="true" />
      <span className="sr-only">{alt}</span>
    </div>
  );
}

function summaryFor(state: TargetCoverState | null | undefined): TargetCoverSummary {
  const options = nativeOptions(state?.nativeOptions);
  const mode = options.cover?.mode ?? 'provider_default';
  const stale = state?.coverAsset?.status === 'stale';
  const selectedWithoutAsset = mode !== 'provider_default' && !state?.coverAsset;
  return {
    label: stale ? 'Cover needs review' : modeLabel(mode),
    mode,
    blocking: stale || selectedWithoutAsset,
    issue: stale
      ? 'The video changed after this cover was selected.'
      : selectedWithoutAsset
        ? 'The selected cover is not ready.'
        : null,
    previewUrl: state?.coverAsset?.publicUrl ?? null,
  };
}

export function TargetCoverEditor({
  targetId,
  videoUrl,
  durationSeconds,
  focused = false,
  onReview,
  onDone,
  onCancel,
  onSummaryChange,
}: TargetCoverEditorProps) {
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const trackedExposureRef = useRef<string | null>(null);
  const emittedSummaryRef = useRef<string | null>(null);
  const [mode, setMode] = useState<EditorMode>('auto');
  const [shareToFeed, setShareToFeed] = useState(true);
  const [frameTimeMs, setFrameTimeMs] = useState(1000);
  const [candidateAsset, setCandidateAsset] = useState<TargetCoverAsset | null>(null);
  const [jobId, setJobId] = useState<string | null>(null);
  const [announcement, setAnnouncement] = useState('');
  const [mobilePreview, setMobilePreview] = useState<'reel' | 'profile'>('reel');

  const stateQuery = useQuery({
    queryKey: ['target-cover-state', targetId],
    enabled: Boolean(targetId),
    queryFn: async () => {
      const result = await targetCoverService.getState(targetId!);
      if (result.error) throw new Error(result.error.message);
      return result.data;
    },
    staleTime: 5_000,
  });
  const state = stateQuery.data;
  const options = useMemo(() => nativeOptions(state?.nativeOptions), [state?.nativeOptions]);
  const currentMode = options.cover?.mode ?? 'provider_default';
  const savedFrameTimeMs = options.cover?.frameTimeMs;
  const savedShareToFeed = options.shareToFeed;
  const stateTargetId = state?.targetId;
  const stateVersion = state?.version;
  const stateCoverAsset = state?.coverAsset;
  const statePlatform = state?.platform;
  const statePlacement = state?.placement;

  const assetsQuery = useQuery({
    queryKey: ['target-cover-assets', state?.revisionId],
    enabled: Boolean(state?.revisionId),
    queryFn: async () => {
      const result = await targetCoverService.getRevisionAssets(state!.revisionId);
      if (result.error) throw new Error(result.error.message);
      return result.data ?? [];
    },
  });
  const automaticAsset = useMemo(
    () => assetsQuery.data?.find((asset) => asset.origin === 'auto') ?? null,
    [assetsQuery.data],
  );

  const jobQuery = useQuery({
    queryKey: ['target-cover-job', jobId],
    enabled: Boolean(jobId),
    queryFn: async () => {
      const result = await targetCoverService.getJob(jobId!);
      if (result.error) throw new Error(result.error.message);
      return result.data;
    },
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      return status === 'ready' || status === 'failed' ? false : 1000;
    },
  });

  useEffect(() => {
    if (!state) return;
    setMode(editorModeFor(currentMode));
    setShareToFeed(savedShareToFeed !== false);
    setFrameTimeMs(
      savedFrameTimeMs ??
        stateCoverAsset?.frameTimeMs ??
        Math.max(0, Math.round((durationSeconds ?? 2) * 600)),
    );
    setCandidateAsset(stateCoverAsset ?? null);
    setJobId(null);
  }, [
    currentMode,
    durationSeconds,
    savedFrameTimeMs,
    savedShareToFeed,
    state,
    stateCoverAsset,
    stateTargetId,
    stateVersion,
  ]);

  const coverJobAssetId = jobQuery.data?.coverAssetId;
  const coverJobFrameTimeMs = jobQuery.data?.frameTimeMs;
  const coverJobSafeErrorCode = jobQuery.data?.safeErrorCode;
  const coverJobStatus = jobQuery.data?.status;
  useEffect(() => {
    if (coverJobStatus !== 'ready' || !coverJobAssetId) return;
    let cancelled = false;
    void targetCoverService.getAsset(coverJobAssetId).then((result) => {
      if (cancelled || result.error || !result.data) return;
      setCandidateAsset(result.data);
      setMode('frame');
      setAnnouncement(`Frame at ${formatTimestamp(coverJobFrameTimeMs ?? 0)} is ready.`);
      void trackProductEvent('target_cover_frame_ready', {
        platform: statePlatform,
        placement: statePlacement,
      });
    });
    return () => {
      cancelled = true;
    };
  }, [coverJobAssetId, coverJobFrameTimeMs, coverJobStatus, statePlacement, statePlatform]);

  useEffect(() => {
    if (coverJobStatus !== 'failed') return;
    setAnnouncement('That frame could not be prepared. Choose another frame or keep Automatic.');
    void trackProductEvent('target_cover_frame_failed', {
      platform: statePlatform,
      placement: statePlacement,
      code: coverJobSafeErrorCode ?? 'cover_job_failed',
    });
  }, [coverJobSafeErrorCode, coverJobStatus, statePlacement, statePlatform]);

  const placement = state?.placement as Placement | undefined;
  const capability = useMemo(
    () =>
      statePlatform && placement
        ? resolveNativeVideoCoverCapability({
            platform: statePlatform,
            placement,
            mediaKind: 'video',
          })
        : null,
    [placement, statePlatform],
  );
  const summary = useMemo(() => {
    const base = summaryFor(state);
    const readiness = evaluateNativeVideoCoverReadiness({
      capability,
      mode: currentMode,
      coverAssetStatus: state?.coverAsset?.status ?? null,
      sourceRevisionId: options.cover?.sourceRevisionId ?? null,
      targetRevisionId: state?.revisionId ?? null,
      frameTimeMs: options.cover?.frameTimeMs ?? null,
      shareToFeed: options.shareToFeed === true,
      hasProfileSurface: options.profileSurface?.ratio === '3:4',
    });
    return {
      ...base,
      blocking: !readiness.canPublish,
      issue: readiness.message,
    };
  }, [capability, currentMode, options, state]);
  useEffect(() => {
    const signature = JSON.stringify(summary);
    if (emittedSummaryRef.current === signature) return;
    emittedSummaryRef.current = signature;
    onSummaryChange?.(summary);
  }, [onSummaryChange, summary]);

  const canEdit = state ? ['unscheduled', 'scheduled'].includes(state.deliveryStatus) : false;
  useEffect(() => {
    if (!state || !capability || capability.modes.length === 1) return;
    const exposureKey = `${state.targetId}:${state.version}`;
    if (trackedExposureRef.current === exposureKey) return;
    trackedExposureRef.current = exposureKey;
    void trackProductEvent('target_cover_row_viewed', {
      platform: state.platform,
      placement: state.placement,
      currentMode,
    });
  }, [capability, currentMode, state]);
  const maxFrameMs = Math.max(1000, Math.round((durationSeconds ?? 60) * 1000));
  const selectedAsset =
    mode === 'auto'
      ? (automaticAsset ?? (currentMode === 'auto' ? (state?.coverAsset ?? null) : null))
      : candidateAsset?.origin === mode
        ? candidateAsset
        : null;
  const previewUrl =
    selectedAsset?.publicUrl ?? (mode === editorModeFor(currentMode) ? summary.previewUrl : null);
  const framePreparing =
    Boolean(jobId) && !['ready', 'failed'].includes(jobQuery.data?.status ?? '');

  const frameMutation = useMutation({
    mutationFn: async () => {
      if (!state) throw new Error('Cover target is unavailable');
      const correlationId = crypto.randomUUID();
      const result = await targetCoverService.prepareFrame({
        targetId: state.targetId,
        expectedTargetVersion: state.version,
        frameTimeMs,
        idempotencyKey: `frame:${state.targetId}:${state.version}:${frameTimeMs}:${crypto.randomUUID()}`,
        correlationId,
      });
      if (result.error) throw new Error(result.error.message);
      return result.data!;
    },
    onSuccess: (job) => {
      setJobId(job.jobId);
      setAnnouncement(`Preparing the frame at ${formatTimestamp(frameTimeMs)}.`);
      void trackProductEvent('target_cover_frame_requested', {
        platform: state?.platform,
        placement: state?.placement,
      });
    },
    onError: (error: Error) => {
      setAnnouncement(error.message);
      toast.error(error.message);
    },
  });

  const uploadMutation = useMutation({
    mutationFn: async (file: File) => {
      if (!state) throw new Error('Cover target is unavailable');
      const result = await targetCoverService.uploadCover({
        targetId: state.targetId,
        expectedTargetVersion: state.version,
        file,
        idempotencyKey: `upload:${state.targetId}:${state.version}:${crypto.randomUUID()}`,
      });
      if (result.error) throw new Error(result.error.message);
      return result.data!;
    },
    onSuccess: (uploaded) => {
      setCandidateAsset({
        id: uploaded.coverAssetId,
        publicUrl: uploaded.publicUrl,
        origin: 'upload',
        frameTimeMs: null,
        width: uploaded.width,
        height: uploaded.height,
        status: 'ready',
      });
      setMode('upload');
      setAnnouncement('Uploaded cover is ready to use.');
      void trackProductEvent('target_cover_upload_ready', {
        platform: state?.platform,
        placement: state?.placement,
      });
    },
    onError: (error: Error) => {
      setAnnouncement(error.message);
      toast.error(error.message);
    },
  });

  const saveMutation = useMutation({
    mutationFn: async () => {
      if (!state) throw new Error('Cover target is unavailable');
      const asset = selectedAsset;
      const resolvedMode: NativeVideoCoverMode =
        mode === 'auto' ? (asset?.origin === 'auto' ? 'auto' : 'provider_default') : mode;
      if (resolvedMode !== 'provider_default' && !asset) {
        throw new Error(
          mode === 'frame'
            ? 'Prepare this frame before using it.'
            : 'Upload a cover before using it.',
        );
      }
      const cover =
        resolvedMode === 'provider_default'
          ? {
              mode: 'provider_default',
              sourceRevisionId: state.revisionId,
              reviewedAt: new Date().toISOString(),
            }
          : {
              mode: resolvedMode,
              coverAssetId: asset!.id,
              sourceRevisionId: state.revisionId,
              ...(resolvedMode === 'frame'
                ? { frameTimeMs: asset!.frameTimeMs ?? frameTimeMs }
                : {}),
              reviewedAt: new Date().toISOString(),
            };
      const profileSurface =
        state.platform === 'instagram' && state.placement === 'reel'
          ? {
              mode: 'provider_fixed_preview',
              ratio: '3:4',
              ...(asset ? { coverAssetId: asset.id } : {}),
              focalX: 0.5,
              focalY: 0.5,
              zoom: 1,
              policyVersion: 'instagram-grid-3x4-v1',
            }
          : null;
      const result = await targetCoverService.saveNativeOptions({
        targetId: state.targetId,
        expectedTargetVersion: state.version,
        patch: {
          cover,
          ...(capability?.shareToFeed ? { shareToFeed } : {}),
          ...(state.platform === 'instagram' && state.placement === 'reel'
            ? { profileSurface }
            : {}),
        },
        idempotencyKey: `save-cover:${state.targetId}:${state.version}:${crypto.randomUUID()}`,
        correlationId: crypto.randomUUID(),
      });
      if (result.error) throw new Error(result.error.message);
      return { resolvedMode };
    },
    onSuccess: async ({ resolvedMode }) => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['target-cover-state', targetId] }),
        queryClient.invalidateQueries({ queryKey: ['target-cover-assets', state?.revisionId] }),
        queryClient.invalidateQueries({ queryKey: ['post-cover-targets'] }),
        queryClient.invalidateQueries({ queryKey: ['instagram-feed-plan'] }),
      ]);
      setAnnouncement('Cover saved.');
      toast.success('Cover saved');
      void trackProductEvent('target_cover_saved', {
        platform: state?.platform,
        placement: state?.placement,
        mode: resolvedMode,
        shareToFeed,
      });
      onDone?.();
    },
    onError: (error: Error) => {
      setAnnouncement(error.message);
      toast.error(error.message);
      void stateQuery.refetch();
    },
  });

  if (!focused) {
    if (!targetId) {
      return (
        <div className="flex min-h-14 items-center gap-3 rounded-lg border border-border/40 bg-muted/20 px-3 py-2.5">
          <div className="flex size-10 shrink-0 items-center justify-center rounded-md bg-muted/50 text-muted-foreground">
            <SpinnerGapIcon className="size-4 animate-spin" aria-hidden="true" />
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-caption font-medium text-foreground">Preparing cover options</p>
            <p className="text-detail text-muted-foreground">Resolving the exact destination.</p>
          </div>
        </div>
      );
    }
    if (stateQuery.isError || !state) {
      return (
        <div className="flex min-h-14 items-center gap-3 rounded-lg border border-destructive/30 bg-destructive/[0.04] px-3 py-2.5">
          <WarningCircleIcon className="size-5 shrink-0 text-destructive" aria-hidden="true" />
          <div className="min-w-0 flex-1">
            <p className="text-caption font-medium text-foreground">Cover unavailable</p>
            <p className="text-detail text-muted-foreground">Reload this post and try again.</p>
          </div>
        </div>
      );
    }
    if (!capability || capability.modes.length === 1) return null;
    return (
      <div className="flex min-h-14 items-center gap-3 rounded-lg border border-border/40 bg-muted/20 px-3 py-2.5">
        <div className="size-10 shrink-0 overflow-hidden rounded-md bg-muted/50">
          <CoverVisual
            imageUrl={summary.previewUrl}
            videoUrl={videoUrl}
            frameTimeMs={state.coverAsset?.frameTimeMs ?? 1000}
            alt="Current video cover"
          />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-caption font-medium text-foreground">Cover</p>
          <p
            className={cn(
              'text-detail',
              summary.blocking ? 'text-destructive' : 'text-muted-foreground',
            )}
          >
            {summary.issue ?? summary.label}
          </p>
        </div>
        {canEdit ? (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="shrink-0"
            onClick={() => {
              void trackProductEvent('target_cover_editor_opened', {
                platform: state.platform,
                placement: state.placement,
                currentMode,
              });
              onReview?.();
            }}
          >
            Review
          </Button>
        ) : null}
      </div>
    );
  }

  const closeEditor = (saved: boolean) => {
    if (!saved) {
      void trackProductEvent('target_cover_editor_cancelled', {
        platform: state?.platform,
        placement: state?.placement,
      });
    }
    onCancel?.();
  };

  return (
    <div className="flex h-full min-h-0 flex-col overflow-hidden bg-background">
      <div className="flex h-12 shrink-0 items-center gap-2 border-b border-border/40 px-4">
        <CircleIconButton size="touch" ariaLabel="Back to post" onClick={() => closeEditor(false)}>
          <ArrowLeftIcon weight="regular" />
        </CircleIconButton>
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-medium text-foreground">Video cover</p>
          <p className="truncate text-detail text-muted-foreground">
            {state
              ? `${state.platform === 'instagram' ? 'Instagram' : 'TikTok'}${state.accountLabel ? ` · ${state.accountLabel}` : ''} · ${state.placement}`
              : 'Loading destination'}
          </p>
        </div>
      </div>

      {stateQuery.isLoading ? (
        <div className="flex min-h-0 flex-1 items-center justify-center text-muted-foreground">
          <SpinnerGapIcon className="size-5 animate-spin" aria-label="Loading cover editor" />
        </div>
      ) : stateQuery.isError || !state || !capability ? (
        <div className="flex min-h-0 flex-1 flex-col items-center justify-center gap-2 px-6 text-center">
          <WarningCircleIcon className="size-6 text-destructive" aria-hidden="true" />
          <p className="text-sm font-medium text-foreground">Cover options are unavailable</p>
          <p className="max-w-sm text-detail text-muted-foreground">
            Mora could not verify this exact destination. Return to the post and reload it.
          </p>
        </div>
      ) : (
        <div className="grid min-h-0 flex-1 md:grid-cols-[minmax(0,1fr)_360px]">
          <div className="min-h-0 overflow-y-auto bg-muted/10 px-4 py-5 md:px-6">
            <div className="mx-auto flex min-h-full max-w-3xl flex-col justify-center gap-4">
              <div className="flex items-center justify-between gap-3">
                <p className={SECTION_LABEL}>Preview</p>
                <span className="text-detail text-muted-foreground">Preview only</span>
              </div>
              {state.platform === 'instagram' && shareToFeed ? (
                <div
                  className="mx-auto grid w-full max-w-[280px] grid-cols-2 rounded-lg bg-muted/50 p-1 sm:hidden"
                  role="group"
                  aria-label="Instagram preview surface"
                >
                  <Button
                    type="button"
                    variant={mobilePreview === 'reel' ? 'secondary' : 'ghost'}
                    size="sm"
                    className="min-h-11"
                    aria-pressed={mobilePreview === 'reel'}
                    onClick={() => setMobilePreview('reel')}
                  >
                    Reels tab
                  </Button>
                  <Button
                    type="button"
                    variant={mobilePreview === 'profile' ? 'secondary' : 'ghost'}
                    size="sm"
                    className="min-h-11"
                    aria-pressed={mobilePreview === 'profile'}
                    onClick={() => setMobilePreview('profile')}
                  >
                    Profile grid
                  </Button>
                </div>
              ) : null}
              <div
                className={cn(
                  'grid items-start justify-center gap-4',
                  state.platform === 'instagram' && shareToFeed
                    ? 'grid-cols-1 sm:grid-cols-2'
                    : 'grid-cols-1',
                )}
              >
                <figure
                  className={cn(
                    'mx-auto w-full max-w-[280px] space-y-2',
                    state.platform === 'instagram' &&
                      shareToFeed &&
                      mobilePreview === 'profile' &&
                      'hidden sm:block',
                  )}
                >
                  <div className="aspect-[9/16] overflow-hidden rounded-xl border border-border/40 bg-muted/30">
                    <CoverVisual
                      imageUrl={previewUrl}
                      videoUrl={videoUrl}
                      frameTimeMs={frameTimeMs}
                      alt="Reel cover preview"
                    />
                  </div>
                  <figcaption className="text-center text-detail text-muted-foreground">
                    {state.platform === 'instagram' ? 'Reels tab' : 'Video cover'}
                  </figcaption>
                </figure>
                {state.platform === 'instagram' && shareToFeed ? (
                  <figure
                    className={cn(
                      'mx-auto w-full max-w-[280px] space-y-2',
                      mobilePreview === 'reel' && 'hidden sm:block',
                    )}
                  >
                    <div className="aspect-[3/4] overflow-hidden rounded-xl border border-border/40 bg-muted/30">
                      <CoverVisual
                        imageUrl={previewUrl}
                        videoUrl={videoUrl}
                        frameTimeMs={frameTimeMs}
                        alt="Instagram profile grid preview"
                      />
                    </div>
                    <figcaption className="text-center text-detail text-muted-foreground">
                      Profile grid · fixed 3:4 preview
                    </figcaption>
                  </figure>
                ) : null}
              </div>
              {state.platform === 'instagram' && shareToFeed ? (
                <p className="mx-auto max-w-lg text-center text-detail leading-relaxed text-muted-foreground">
                  Instagram controls the final profile crop. Mora shows the current fixed 3:4 policy
                  and does not send a separate focal-point setting.
                </p>
              ) : null}
            </div>
          </div>

          <div className="min-h-0 overflow-y-auto border-t border-border/40 px-4 py-5 md:border-l md:border-t-0">
            <div className="space-y-6">
              <div className="space-y-1">
                <p className={SECTION_LABEL}>Cover source</p>
                <p className="text-detail leading-relaxed text-muted-foreground">
                  Automatic stays zero-click. Choose a frame or upload an image only when this post
                  needs it.
                </p>
              </div>

              <RadioGroup
                value={mode}
                onValueChange={(value) => {
                  const next = value as EditorMode;
                  setMode(next);
                  setAnnouncement(
                    `${next === 'auto' ? 'Automatic cover' : next === 'frame' ? 'Frame selection' : 'Upload'} selected.`,
                  );
                  void trackProductEvent('target_cover_mode_selected', {
                    platform: state.platform,
                    placement: state.placement,
                    mode: next,
                  });
                }}
                aria-label="Cover source"
                className="gap-2"
              >
                <label
                  className={cn(
                    'flex min-h-11 cursor-pointer items-start gap-3 rounded-lg border p-3',
                    mode === 'auto' ? 'border-foreground/25 bg-muted/40' : 'border-border/40',
                  )}
                >
                  <RadioGroupItem value="auto" className="mt-0.5" />
                  <span>
                    <span className="block text-caption font-medium text-foreground">
                      Automatic
                    </span>
                    <span className="block text-detail text-muted-foreground">
                      Use Mora&apos;s poster or let the network choose.
                    </span>
                  </span>
                </label>
                {capability.modes.includes('frame') ? (
                  <label
                    className={cn(
                      'flex min-h-11 cursor-pointer items-start gap-3 rounded-lg border p-3',
                      mode === 'frame' ? 'border-foreground/25 bg-muted/40' : 'border-border/40',
                    )}
                  >
                    <RadioGroupItem value="frame" className="mt-0.5" />
                    <span>
                      <span className="block text-caption font-medium text-foreground">
                        Choose a frame
                      </span>
                      <span className="block text-detail text-muted-foreground">
                        Pick a moment from this video.
                      </span>
                    </span>
                  </label>
                ) : null}
                {capability.modes.includes('upload') ? (
                  <label
                    className={cn(
                      'flex min-h-11 cursor-pointer items-start gap-3 rounded-lg border p-3',
                      mode === 'upload' ? 'border-foreground/25 bg-muted/40' : 'border-border/40',
                    )}
                  >
                    <RadioGroupItem value="upload" className="mt-0.5" />
                    <span>
                      <span className="block text-caption font-medium text-foreground">
                        Upload an image
                      </span>
                      <span className="block text-detail text-muted-foreground">
                        JPEG, PNG, or WebP up to 8 MB.
                      </span>
                    </span>
                  </label>
                ) : null}
              </RadioGroup>

              {mode === 'frame' ? (
                <div className="space-y-3 rounded-lg border border-border/40 p-3">
                  <div className="flex items-center justify-between gap-3">
                    <label
                      htmlFor="target-cover-frame"
                      className="text-caption font-medium text-foreground"
                    >
                      Frame
                    </label>
                    <span className="tabular-nums text-detail text-muted-foreground">
                      {formatTimestamp(frameTimeMs)}
                    </span>
                  </div>
                  <Slider
                    id="target-cover-frame"
                    min={0}
                    max={maxFrameMs}
                    step={100}
                    value={[frameTimeMs]}
                    onValueChange={([value]) => setFrameTimeMs(value)}
                    aria-label="Video frame time"
                    aria-valuetext={formatTimestamp(frameTimeMs)}
                    className="min-h-11 py-3"
                    showTooltip
                    tooltipContent={formatTimestamp}
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="w-full"
                    disabled={frameMutation.isPending || framePreparing}
                    onClick={() => frameMutation.mutate()}
                  >
                    {frameMutation.isPending || framePreparing ? (
                      <SpinnerGapIcon className="size-3.5 animate-spin" />
                    ) : (
                      <FilmStripIcon className="size-3.5" />
                    )}
                    {framePreparing ? 'Preparing frame…' : 'Use this frame'}
                  </Button>
                </div>
              ) : null}

              {mode === 'upload' ? (
                <div className="space-y-3 rounded-lg border border-border/40 p-3">
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    className="sr-only"
                    onChange={(event) => {
                      const file = event.currentTarget.files?.[0];
                      if (file) uploadMutation.mutate(file);
                      event.currentTarget.value = '';
                    }}
                  />
                  <div className="flex items-start gap-3">
                    <ImageSquareIcon className="mt-0.5 size-5 shrink-0 text-muted-foreground" />
                    <div className="min-w-0 flex-1">
                      <p className="text-caption font-medium text-foreground">Custom image</p>
                      <p className="text-detail text-muted-foreground">
                        Use a clear portrait image that still reads at thumbnail size.
                      </p>
                    </div>
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="w-full"
                    disabled={uploadMutation.isPending}
                    onClick={() => fileInputRef.current?.click()}
                  >
                    {uploadMutation.isPending ? (
                      <SpinnerGapIcon className="size-3.5 animate-spin" />
                    ) : (
                      <UploadSimpleIcon className="size-3.5" />
                    )}
                    {candidateAsset?.origin === 'upload' ? 'Replace image' : 'Choose image'}
                  </Button>
                </div>
              ) : null}

              {capability.shareToFeed ? (
                <div className="flex min-h-11 items-start justify-between gap-4 rounded-lg border border-border/40 p-3">
                  <div>
                    <label
                      htmlFor="share-reel-to-profile"
                      className="text-caption font-medium text-foreground"
                    >
                      Show on profile
                    </label>
                    <p className="mt-0.5 text-detail leading-relaxed text-muted-foreground">
                      Share this Reel to the Instagram feed and profile grid.
                    </p>
                  </div>
                  <Switch
                    id="share-reel-to-profile"
                    checked={shareToFeed}
                    onCheckedChange={setShareToFeed}
                    className="my-2"
                  />
                </div>
              ) : null}

              <p aria-live="polite" className="min-h-4 text-detail text-muted-foreground">
                {announcement}
              </p>
            </div>
          </div>
        </div>
      )}

      <div className="flex min-h-12 shrink-0 items-center justify-between gap-3 border-t border-border/40 px-4 py-2">
        <Button type="button" variant="ghost" size="sm" onClick={() => closeEditor(false)}>
          Cancel
        </Button>
        <Button
          type="button"
          variant="neutral"
          size="sm"
          disabled={
            !canEdit ||
            saveMutation.isPending ||
            stateQuery.isLoading ||
            !state ||
            ((mode === 'frame' || mode === 'upload') && !selectedAsset)
          }
          onClick={() => saveMutation.mutate()}
        >
          {saveMutation.isPending ? (
            <SpinnerGapIcon className="size-3.5 animate-spin" />
          ) : (
            <CheckIcon className="size-3.5" weight="bold" />
          )}
          Save cover
        </Button>
      </div>
    </div>
  );
}
